/**
 * Dashboard layout (APP-001 §7, §14).
 *
 * Wraps the dashboard page outlet in the AppShell and publishes the resolved
 * backend identity into context so descendant pages and the AccountMenu can
 * read safe account metadata without re-fetching. The identity arrives here
 * as a prop from <AccountBootstrap>'s render-prop (see ProtectedRoute).
 */
import { createContext, useContext, type ReactNode } from 'react';
import { AppShell } from '@/components/navigation/AppShell';
import type { DashboardIdentityResponse } from '@/api/dashboard-types';

const IdentityContext = createContext<DashboardIdentityResponse | null>(null);

export function useDashboardIdentity(): DashboardIdentityResponse {
  const identity = useContext(IdentityContext);
  if (!identity) {
    throw new Error('useDashboardIdentity must be used within <DashboardLayout>.');
  }
  return identity;
}

export function DashboardLayout({
  identity,
  children,
  variant = 'app',
}: {
  identity: DashboardIdentityResponse;
  children: ReactNode;
  /** 'focus' hides primary navigation (onboarding trap); default 'app'. */
  variant?: 'app' | 'focus';
}) {
  return (
    <IdentityContext.Provider value={identity}>
      <AppShell identity={identity} variant={variant}>
        {children}
      </AppShell>
    </IdentityContext.Provider>
  );
}
